import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { RecaptchaService } from '../service/recaptcha/recaptcha.service';

@Injectable()
export class RecaptchaInterceptor implements HttpInterceptor {

  private protectedEndpoints: string[] = [
    '/api/portal/auth/login',
    '/api/portal/auth/signup',
    '/api/portal/auth/forgotpassword',
    '/api/admin/auth/login',
    '/api/portal/order/order',
    '/api/portal/order/orderforeign',
    '/api/portal/contact/sent',
    '/api/portal/promotion/sent'
  ];

  constructor(private recaptchaService: RecaptchaService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const url = request.url || '';

    // IMPORTANTE: NO interceptar URLs externas (Niubiz, servicios de pago, etc.)
    if (this.isExternalUrl(url)) {
      console.debug('[RecaptchaInterceptor] URL externa detectada, skip reCAPTCHA:', url);
      return next.handle(request);
    }

    const requiresRecaptcha = this.shouldAddRecaptcha(url);

    // <-- SKIP preflight and other non-POST methods
    if (request.method === 'OPTIONS') {
      // Do not attempt to get token for preflight
      return next.handle(request);
    }

    // Optionally only for POST if your endpoints are POSTs
    if (request.method !== 'POST') {
      return next.handle(request);
    }

    console.debug('[RecaptchaInterceptor] URL=', url, 'requiresRecaptcha=', requiresRecaptcha, 'enabled=', this.recaptchaService.isEnabled());

    if (!requiresRecaptcha) {
      return next.handle(request);
    }

    if (!this.recaptchaService.isEnabled()) {
      // Si reCAPTCHA está deshabilitado, dejar pasar la petición sin token
      console.debug('[RecaptchaInterceptor] reCAPTCHA deshabilitado, continuando sin token');
      return next.handle(request);
    }

    return from(this.recaptchaService.execute(this.getActionName(url))).pipe(
      switchMap(token => {
        if (!token) {
          return throwError(() => new HttpErrorResponse({
            status: 400,
            statusText: 'No Token',
            url,
            error: { message: 'Token reCAPTCHA vacío' }
          }));
        }
        console.debug('[RecaptchaInterceptor] token obtenido (trunc):', token.substring?.(0,8) + '...');

        // SOLUCIÓN ALTERNATIVA: Agregar token al BODY en lugar del header (evita CORS)
        let modifiedBody = request.body;
        if (modifiedBody && typeof modifiedBody === 'object') {
          // Clonar el body y agregar el token
          modifiedBody = { ...modifiedBody, recaptchaToken: token };
        }

        const clonedRequest = request.clone({
          body: modifiedBody
        });

        return next.handle(clonedRequest);
      }),
      catchError((err) => {
        console.error('[RecaptchaInterceptor] Error al obtener token de reCAPTCHA:', err);
        const errorResp = (err instanceof HttpErrorResponse) ? err : new HttpErrorResponse({
          status: 500,
          statusText: 'reCAPTCHA Error',
          url,
          error: { message: err?.message || 'Error al obtener token de reCAPTCHA' }
        });
        return throwError(() => errorResp);
      })
    );
  }

  private shouldAddRecaptcha(url: string): boolean {
    if (!url) return false;
    return this.protectedEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Detecta si la URL es externa (Niubiz, servicios de pago, etc.)
   * NO aplicar reCAPTCHA a URLs externas
   */
  private isExternalUrl(url: string): boolean {
    if (!url) return false;

    // Lista de dominios/paths externos que NO deben tener reCAPTCHA
    const externalPatterns = [
      'vnforapps.com',           // Niubiz (pasarela de pago)
      'niubiz.com.pe',           // Niubiz alternativo
      'google.com/recaptcha',    // Google reCAPTCHA API
      'visanet.com.pe',          // Visanet
      'pagoefectivo.pe',         // PagoEfectivo
      'izipay.pe',               // IziPay
      'mercadopago.com',         // MercadoPago
    ];

    // Verificar si la URL contiene algún patrón externo
    return externalPatterns.some(pattern => url.includes(pattern));
  }

  private getActionName(url: string): string {
    if (url.includes('/admin/auth/login')) return 'admin_login';
    if (url.includes('/portal/auth/login')) return 'user_login';
    if (url.includes('/signup')) return 'register';
    if (url.includes('/forgotpassword')) return 'forgot_password';
    if (url.includes('/orderforeign')) return 'order_foreign';
    if (url.includes('/order/order')) return 'order';
    if (url.includes('/contact')) return 'contact';
    if (url.includes('/promotion')) return 'promotion';
    return 'submit';
  }
}
